import { useState } from "react";
import { GitCommit, Sparkles, Check, Clock, AlertCircle } from "lucide-react";
import {
  Timeline,
  type TimelineItem,
  type TimelineProps,
  Badge,
  Button,
} from "@/shared/ui";

export function TestUiCoreTimelineShowcase() {
  const [variant, setVariant] = useState<"ring" | "icon" | "dot">("ring");
  const [lineStyle, setLineStyle] = useState<"solid" | "dashed">("solid");
  const [isDense, setIsDense] = useState(false);

  // 1. Phê duyệt quy trình BOM (Chuẩn theo screenshot người dùng)
  const approvalJourneyItems: TimelineItem[] = [
    {
      id: "step-1",
      time: "05/09/2026 15:27:51",
      title: "Gửi phê duyệt",
      subtitle:
        "Người gửi: admin · Policy: POL-ENG-BOM · Phê duyệt BOM — 1 cấp BGD/QA",
      status: "neutral",
    },
    {
      id: "step-2",
      time: "—",
      title: "Cấp 1 (đang chờ)",
      subtitle:
        "Chưa có người duyệt · Vai trò: ROLE_BGD, ROLE_QA, ROLE_ADMIN",
      status: "warning",
      badge: {
        label: "Đang chờ",
        tone: "warning",
      },
    },
  ];

  // 2. Tiến trình sản xuất & kiểm định nhiều bước phức tạp
  const manufacturingJourneyItems: TimelineItem[] = [
    {
      id: "p1",
      time: "29/09/2026 08:15:00",
      title: "Tiếp nhận nguyên vật liệu cá fillet",
      subtitle: "Người phụ trách: Nguyễn Văn An (Kho lạnh A1)",
      status: "success",
      badge: { label: "Đã hoàn thành", tone: "success" },
    },
    {
      id: "p2",
      time: "29/09/2026 10:30:20",
      title: "Kiểm nghiệm vi sinh & hóa nghiệm CCP1",
      subtitle: "Chuyên viên QA: Trần Thị Mai · Phòng lab QA-02",
      status: "success",
      badge: { label: "Đạt chuẩn", tone: "success" },
      detail:
        "Ghi chú QA: Mẫu kiểm thử đạt chỉ tiêu vi sinh TCVN 5603:2008. Cho phép chuyển sang công đoạn tẩm gia vị.",
    },
    {
      id: "p3",
      time: "29/09/2026 13:45:00",
      title: "Đóng gói hút chân không & Cấp đông sâu",
      subtitle: "Phân xưởng Chế biến 2 · Dây chuyền IQF-03",
      status: "primary",
      badge: { label: "Đang xử lý", tone: "primary" },
    },
    {
      id: "p4",
      time: "Dự kiến 16:30",
      title: "Kiểm tra dò kim loại & Đóng thùng thành phẩm",
      subtitle: "KCS Cuối chuyền · Máy dò Anritsu M6",
      status: "neutral",
      badge: { label: "Kế hoạch", tone: "neutral" },
    },
  ];

  return (
    <section className="rounded-control border border-border bg-surface p-5 shadow-xs space-y-4">
      {/* Header */}
      <div className="border-b border-border/60 pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2">
              <GitCommit className="size-5 text-primary" />
              7. Tiến Trình Dọc / Lịch Sử Quy Trình (Timeline / VerticalTimeline)
            </h3>
            <p className="mt-1 mb-0 text-caption text-text-muted">
              Chuẩn hóa giao diện tiến trình dọc (Approval Journey, Shipment Tracking, Batch MES, Audit Logs) với các kiểu đầu mút:
              <strong> Hollow Ring</strong> (vòng khuyên rỗng), <strong>Solid Dot</strong>, hoặc <strong>Status Icon</strong>.
            </p>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <Badge tone="success" label="Core Component Mới" />
            <Badge tone="primary" label="@/shared/ui" />
          </div>
        </div>

        {/* Interactive Controls Toolbar */}
        <div className="mt-3 flex flex-wrap items-center gap-3 rounded-control bg-bg/80 p-2.5 text-caption">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-text">Kiểu điểm mốc:</span>
            <div className="inline-flex rounded-control border border-border bg-surface p-0.5 shadow-2xs">
              {(["ring", "icon", "dot"] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setVariant(mode)}
                  className={`px-2.5 py-1 text-caption font-medium rounded-control transition-all cursor-pointer ${variant === mode
                      ? "bg-primary text-on-primary font-bold shadow-xs"
                      : "text-text-secondary hover:text-text hover:bg-border/40"
                    }`}
                >
                  {mode === "ring" ? "Vòng rỗng (Mẫu)" : mode === "icon" ? "Biểu tượng" : "Chấm tròn"}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="font-semibold text-text">Đường nối:</span>
            <div className="inline-flex rounded-control border border-border bg-surface p-0.5 shadow-2xs">
              {(["solid", "dashed"] as const).map((style) => (
                <button
                  key={style}
                  type="button"
                  onClick={() => setLineStyle(style)}
                  className={`px-2.5 py-1 text-caption font-medium rounded-control transition-all cursor-pointer ${lineStyle === style
                      ? "bg-primary text-on-primary font-bold shadow-xs"
                      : "text-text-secondary hover:text-text hover:bg-border/40"
                    }`}
                >
                  {style === "solid" ? "Nét liền" : "Nét đứt"}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 cursor-pointer font-semibold text-text select-none">
              <input
                type="checkbox"
                checked={isDense}
                onChange={(e) => setIsDense(e.target.checked)}
                className="size-3.5 rounded text-primary focus:ring-primary"
              />
              <span>Rút gọn (Dense)</span>
            </label>
          </div>
        </div>
      </div>

      {/* Grid displaying Both Scenarios */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-1">
        {/* Sample 1: Exactly matching user request */}
        <div className="rounded-control border border-border/80 bg-surface p-4 shadow-2xs space-y-3">
          <div className="flex items-center justify-between border-b border-border/50 pb-2">
            <span className="text-body font-bold text-text flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-warning" />
              Lịch sử phê duyệt phiếu (Theo mẫu người dùng)
            </span>
            <span className="text-caption font-mono text-text-muted">2 bước</span>
          </div>

          <Timeline
            items={approvalJourneyItems}
            indicatorVariant={variant}
            lineStyle={lineStyle}
            dense={isDense}
          />
        </div>

        {/* Sample 2: Multi-step ERP Production Flow with QA Remarks */}
        <div className="rounded-control border border-border/80 bg-surface p-4 shadow-2xs space-y-3">
          <div className="flex items-center justify-between border-b border-border/50 pb-2">
            <span className="text-body font-bold text-text flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-success" />
              Tiến trình sản xuất & kiểm nghiệm lô hàng (Nhiều bước + ghi chú)
            </span>
            <span className="text-caption font-mono text-text-muted">4 công đoạn</span>
          </div>

          <Timeline
            items={manufacturingJourneyItems}
            indicatorVariant={variant}
            lineStyle={lineStyle}
            dense={isDense}
          />
        </div>
      </div>
    </section>
  );
}
